import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import ExpandMore from '@material-ui/icons/ArrowDropDown';
import ExpandLess from '@material-ui/icons/ArrowDropUp';
import ClearIcon from '@material-ui/icons/Clear';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import ComponentSelector from '../../application-core/ComponentSelector';
import ActionText from '../../core/ActionText';
import EntitlementWarning from '../../core/EntitlementWarning';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import MenuSelector from '../../core/MenuSelector';
import OptionsSelector from '../../core/OptionsSelector';
import Paging from '../../core/Paging';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import Upgrade from '../../core/Upgrade';
import Warning from '../../core/Warning';
import ProjectSummary from '../../core/project/ProjectSummary';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { globalStyles } from '../../core/style/gstheme';
import { DATATYPES, MAX_DEFINITION_LENGTH } from '../../core/utils/constants';
import { basicCheckIRI, checkValidity, getGraphFromProject, makeCancelable, processHashLocation, sortByProp, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import AddFilterDialog from './AddFilterDialog';
import EditDataDialog from './EditDataDialog';
import SearchFilter from './SearchFilter';
import SearchResults from './SearchResults';
import { DOES_NOT_HAVE_PROPERTY, DOES_NOT_HAVE_PROPERTY_WITH_VALUE, HAS_PROPERTY, HAS_PROPERTY_WITH_VALUE } from './constants';
import SecondaryButton from '../../core/SecondaryButton';
import AccessibleTooltip from '../../core/AccessibleTooltip';

const FILTER_TYPES = [HAS_PROPERTY, HAS_PROPERTY_WITH_VALUE, DOES_NOT_HAVE_PROPERTY, DOES_NOT_HAVE_PROPERTY_WITH_VALUE];
const MAX_FILTER_LENGTH = 2000;
const WARNING_STATE = {
  isWarning: true,
  warningMessage: "Adding this filter is not possible as it will exceed the maximum permitted URL length."
};

const styles = () => {
  return Object.assign({
    advancedSearch: {
      marginRight: 30,
      padding: 12,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      minHeight: 520,
      minWidth: 720
    },
    searchResults: {
      marginTop: 12
    },
    count: {
      backgroundColor: '#CA4F0C',
      borderRadius: '50%',
      color: 'white',
      margin: '0px 0px 0px 6px',
      fontSize: '12px',
      display: 'inline-block',
      width: '20px',
      textAlign: 'center',
      verticalAlign: 'middle',
      height: '16px'
    }
  }, globalStyles);
};

const MAX_RESULTS = 50;

class AdvancedSearchContainer extends Component {

  constructor(props) {
    super(props);
    const subType = GS.getActiveSubscriptionType();
    // If user doesn't have permissions for additional properties then type information will be missing
    const filteredPropsInfo = props.loadInfo ?
      props.loadInfo.filter(li => "type" in li)
      :
      [];
    let additionalOptions = filteredPropsInfo.map(info => {
      let format = "";
      const range = info.range ?
        info.range[0] === "http://www.w3.org/2000/01/rdf-schema#Literal" ?
          DATATYPES.STRING
          :
          info.range[0]
        :
        "";
      if (range === "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString") {
        format = "langString";
      }
      else if (info.type.includes("DatatypeProperty") || info.type.includes("AnnotationProperty")) {
        format = "datatype";
      }
      else {
        format = "relation";
      }
      return {
        graph: info.graph,
        value: info.iri,
        treatContentAs: info.treatContentAs,
        label: info.label ?
          info.label[this.props.defaultLang] ?
            info.label[this.props.defaultLang]
            :
            info.label["und"] ?
              info.label["und"]
              :
              info.id
          :
          info.id,
        visible: false,
        additional: true,
        canFilter: range !== "http://www.w3.org/2008/05/skos-xl#Label" && range !== "http://www.w3.org/2001/XMLSchema#dateTime",
        ...(range) && { range },
        format
      };
    });

    sortByProp(additionalOptions, "label", null, false);

    const viewableOptions = [
      ...this.props.viewOptions,
      {
        heading: true,
        label: "Ontology Properties"
      },
      ...additionalOptions];
    const visibleValues = new Array(this.props.viewOptions.length + additionalOptions.length + 1).fill(true);

    const skipUnfilteredSearches = this.props.data.largeDataSettings ? true : false;

    this.state = {
      fullScreen: false,
      skipUnfilteredSearches,
      pageSize: MAX_RESULTS,
      addFilter: false,
      graphInfo: {},
      componentLinkedProjectsMap: {},
      filters: [],
      filterProperty: "",
      expanded: true,
      filtersURL: "",
      selectedRows: [],
      allowBulkChange: subType === "Managed" || subType === "Trial" || subType === "Premium",
      columns: null,
      visibleValues,
      isLoading: false,
      viewableOptions,
      isLoadingClasses: true,
      lang: this.props.defaultLang,
      resources: [],
      loadInfo: [],
      classResources: [],
      warningMessage: "",
      text: "",
      component: "All",
      searchID: null,
      page: 1,
      noneFound: false,
      searchCount: 0,
      pageCount: 0,
      firstSearch: !skipUnfilteredSearches,
      slowSearch: skipUnfilteredSearches,
      initialSearch: skipUnfilteredSearches,
      isWarning: false,
      isErrored: null
    };
    this.cancellable = null;
    this.cancellable1 = null;
  }

  onExitWarning = () => this.setState({ isWarning: false });

  loadDataFromGraph(iriData, options = {}) {
    this.cancellable1 = makeCancelable(GS.loadDataFromGraph(iriData, options));
    return this.cancellable1;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable1) this.cancellable1.cancel();
    clearTimeout(this.searchTimeout);
  }

  collectResourceData() {
    // First we load association resources
    const opaLoadData = this.props.data.ontologyProjectAssociation ?
      this.props.data.ontologyProjectAssociation.map(opa => {
        return {
          graph: this.props.graph,
          iri: opa
        };
      })
      :
      [];
    this.loadDataFromGraph(opaLoadData, {})
      .promise
      .then(() => {
        // We get all the association information
        const opaSet = this.props.data.ontologyProjectAssociation ?
          this.props.data.ontologyProjectAssociation.map(opa => GS.data(opa, this.props.graph))
          :
          [];
        let loadInfo = [];
        opaSet.forEach(opa => {
          if (opa.associatedClass) {
            const associatedClasses = opa.associatedClass;
            const associatedGraph = getGraphFromProject(opa.associatedProject);
            associatedClasses.forEach(classIRI => loadInfo.push({
              graph: associatedGraph,
              iri: classIRI
            }));
          }
        });
        this.loadDataFromGraph(loadInfo)
          .promise
          .then(() => {
            if (this.cancellable1 && this.cancellable1.isCancelled()) return;
            this.processResources(loadInfo);
            this.setState({ isLoadingClasses: false, loadInfo },
              () => {
                this.handlePageChange();
              }
            );
          })
          .catch((e) => {
            if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
          });
      })
      .catch((e) => {
        if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
      });
  }

  processResources(loadInfo) {
    const classResources = loadInfo
      .filter(info => GS.isLoaded(info.iri, info.graph))
      .map(info => GS.data(info.iri, info.graph));
    sortObjects(classResources, 'label');
    if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ classResources });
  }

  componentDidMount() {
    this.collectResourceData();
  }

  handleSearchTooLong = () => {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    if (!this.state.isLoading) return;
    this.setState({ initialSearch: true, firstSearch: false, slowSearch: true }, () => this.handlePageChange());
  };

  isStillSearching = () => {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(this.handleSearchTooLong, 20000);
  };

  handlePageChange = async () => {
    const noCount = this.state.filters.length === 0 || this.state.initialSearch || (this.state.firstSearch && this.props.optimiseForLargeData);
    if (this.state.skipUnfilteredSearches && this.state.filtersURL === "") {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({
        isLoading: false,
        skipUnfilteredSearches: true,
        firstSearch: false,
        initialSearch: false,
        slowSearch: false,
        page: 1,
        resources: [],
        pageCount: 1,
        searchID: uuidv4(),
        searchCount: 0
      });
      return;
    }
    if (this.state.firstSearch) this.isStillSearching();
    this.setState(
      { isLoading: true },
      () => {
        let search = [
          {
            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
            value: this.props.searchClass
          }
        ];
        if (this.state.filters.length > 0) {
          const searchFilters = this.state.filters.map(f => {
            const viewableProp = this.state.viewableOptions.find(v => v.value === f.property);
            let value = f.value;
            let inferenceGraph = undefined;
            // Compute all subclasses for filter classes
            try {
              if (value && viewableProp.value === "http://www.w3.org/1999/02/22-rdf-syntax-ns#type") {
                const classList = Array.isArray(value) ? value : [value];
                classList.forEach(c => {
                  const classInfo = this.state.loadInfo.find(classInfoItem => classInfoItem.iri === c);
                  if (!classInfo || !("graph" in classInfo)) throw new Error("Invalid class: " + c);
                  // We're assuming only one graph for now
                  inferenceGraph = classInfo.graph;
                });
              }
            }
            catch (e) {
              if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            }
            return {
              predicate: f.property,
              ...(f.value) && { value },
              ...(inferenceGraph) && { inferenceGraph },
              ...(f.filterType === DOES_NOT_HAVE_PROPERTY || f.filterType === DOES_NOT_HAVE_PROPERTY_WITH_VALUE) && { invert: true },
              ...(viewableProp.format === "datatype" || viewableProp.format === "langString") && {
                ...(f.value) && {
                  ...(viewableProp.format === "langString") && { isText: true },
                  isExactMatch: f.isExactMatch
                },
                ...(viewableProp.range && viewableProp.range !== DATATYPES.LANGSTRING) && { datatype: viewableProp.range }
              },
              ...(viewableProp.format === "relation" || viewableProp.format === "class") && {
                isText: false
              },
              ...(viewableProp.format === "class" && f.property === "http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
              && { exclude: this.props.searchClass }
            };
          });
          search = search.concat(searchFilters);
        }
        if (this.state.component !== "All") {
          search.push({
            predicate: this.props.resourceIn,
            value: this.state.component
          });
        }
        const searchID = uuidv4();
        this.setState({ searchID }, () => {
          this.doSearch({
            search: search,
            graph: [this.props.graph],
            noCount,
            fast: this.state.initialSearch || (this.state.firstSearch && this.props.optimiseForLargeData),
            basicSort: true,
            allowMissingResults: true,
            entailment: this.props.skosXL ? ["propertyChain", "skosNote"] : ["skosNote"],
            predicates: [this.props.labelIRI],
            page: this.state.page,
            language: this.state.lang,
            pageSize: this.state.pageSize,
            sort: [this.props.labelIRI]
          }, searchID)
            .promise
            .then(data => {
              if (this.cancellable && this.cancellable.isCancelled()) return;
              // If a status then we probably timed out with a 500
              // Ignore 'old' 
              if (data.uuid && data.uuid !== this.state.searchID) return;
              if (data.status) {
                this.setState({
                  isLoading: false,
                  page: 1,
                  resources: [],
                  pageCount: 1,
                  searchCount: 0,
                  isWarning: true,
                  warningMessage: "The search failed, probably because it took too long to process."
                });
                return;
              }
              if (data.results && data.results.length > 0) {
                const sortedResources = sortObjects([...data.results], this.props.label, this.state.lang, null, false);
                const resourceIDs = sortedResources.map(result => result.id);
                // Load all resources in one go
                this.loadData(resourceIDs, {})
                  .promise
                  .then(async () => {
                    const columnsAsBooleans = this.convertColumns(this.state.columns, this.state.viewableOptions);
                    await this.computeLinkInfo(resourceIDs, columnsAsBooleans);
                    const entailmentData = resourceIDs.map(iri => ({ graph: this.props.graph, iri }));
                    GS.computeEntailment(entailmentData);
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    this.setState({
                      resources: resourceIDs,
                      page: this.state.page,
                      pageCount: noCount ?
                        data.results.length === this.state.pageSize ?
                          this.state.page + 1
                          :
                          0
                        :
                        data.pageCount,
                      searchCount: noCount ? 0 : data.searchCount,
                      isLoading: false
                    });
                  })
                  .catch((e) => {
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                  });
              }
              else {
                this.setState({
                  isLoading: false,
                  page: 1,
                  resources: [],
                  pageCount: 1,
                  searchCount: 0
                });
              }
            })
            .catch((e) => {
              if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
        });
      });
  };

  doSearch(payload, uuid) {
    this.cancellable = makeCancelable(GS.search(payload, uuid, [500]));
    return this.cancellable;
  }

  loadData(iris, options, graph = this.props.graph) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, graph, true));
    return this.cancellable;
  }

  expandFilters = () => this.setState({ expanded: true });

  collapseFilters = () => this.setState({ expanded: false });

  resetFilters = () => {
    const newFilters = [];
    const filtersURL = this.computeFiltersURL(newFilters);
    this.setState({ filters: newFilters }, () => {
      this.updateSearch(1, null, null, null, null, null, filtersURL);
    });
  };

  static getDerivedStateFromProps(props, state) {

    if (state.isLoadingClasses) return null;

    const paramsMap = processHashLocation(props.location);
    const lang = paramsMap.get("lang");
    const text = decodeURIComponent(paramsMap.get("text"));
    const page = paramsMap.get("page");
    const parsedPage = parseInt(page);
    const component = paramsMap.get("component");
    const columns = paramsMap.get("columns");
    const filtersURL = paramsMap.get("filters");
    let pageSize = paramsMap.get("pageSize");
    let filters = [];

    try {
      if (component !== undefined && component !== null && component !== "" && component !== "All") {
        const isValidURL = basicCheckIRI(component);
        if (!isValidURL) throw new Error("Invalid component.");
      }
      if (columns !== undefined && columns !== null && columns !== "") {
        const columnValues = columns.split(",");
        const invalidColumn = columnValues.some((cv, index) => (cv !== "" && cv !== 'true' && cv !== 'false')
          || (cv !== "" && state.viewableOptions[index].heading === true));
        const wrongNumberColumns = columnValues.length !== state.viewableOptions.length;
        if (invalidColumn || wrongNumberColumns) throw new Error("Invalid column data.");
      }
      if (pageSize === undefined || pageSize === null || (pageSize !== "50" && pageSize !== "100" && pageSize !== "200")) {
        pageSize = '50';
      }
      if (filtersURL !== undefined && filtersURL !== null && filtersURL !== "") {
        const filtersList = filtersURL.split(";");
        filters = filtersList.map(item => {
          const parts = item.split(",");
          if (parts.length < 5) throw new Error("Not enough parts in filter.");
          // Will throw error if not a URL
          const property = decodeURIComponent(parts[0]);
          const isValidURL = basicCheckIRI(property);
          if (!isValidURL) throw new Error("Invalid property IRI: " + property);
          const isValidProperty = state.viewableOptions.find(v => v.value === property);
          if (!isValidProperty) throw new Error("Invalid property: " + property);
          const filterType = parts[1];
          const isExactMatch = parts[2];
          const targetGraph = parts[3] === "" ? "" : decodeURIComponent(parts[3]);
          const isValidGraphURL = targetGraph === "" ? true : basicCheckIRI(targetGraph);
          if (!isValidGraphURL) throw new Error("Invalid target graph IRI: " + targetGraph);
          if (isExactMatch !== "" && isExactMatch !== "true" && isExactMatch !== "false") throw new Error("Invalid value for exact match: " + isExactMatch);
          if (!FILTER_TYPES.includes(filterType)) throw new Error("Invalid filter type: " + filterType);

          const value = parts[4] === "" ?
            []
            :
            parts.slice(4).map(v => {
              if (isValidProperty.format === "relation" || isValidProperty.format === "class") {
                const dv = decodeURIComponent(v);
                const isValidURL = basicCheckIRI(dv);
                if (!isValidURL) throw new Error("Expected IRI for filter value: " + dv);
                return dv;
              }
              else
                return decodeURIComponent(v);
            });

          if (value.length > 0) {
            // We need to check that a user hasn't abused the filter values!
            const vo = state.viewableOptions.find(v => v.value === property);
            value.forEach(v => {
              if (v.length > 100) {
                throw new Error("Too long for " + property);
              }
              if (vo.range) {
                let valid = checkValidity(v, vo.range, [], MAX_DEFINITION_LENGTH);
                if (!valid.isValid) throw new Error("Invalid for datatype: " + property);
              }
            });
          }

          if (filterType !== HAS_PROPERTY && filterType !== DOES_NOT_HAVE_PROPERTY && value.length < 1) {
            throw new Error("Invalid value: " + value);
          }

          return {
            property,
            isExactMatch: isExactMatch === 'true',
            filterType,
            targetGraph,
            ...(value.length > 0) && { value }
          };
        });
      }
    }
    catch (e) {
      console.log(e.message) // eslint-disable-line
      const message = e.message || "Invalid URL for search. Somebody may have adjusted the ontology associations for this project which can make URLs invalid.";
      return {
        isErrored: {
          message
        }
      };
    }

    if ((lang !== state.lang) || (text !== state.text) || (page !== state.page) ||
      (component !== state.component) || (columns !== state.columns) || (pageSize !== state.pageSize)) {
      const firstSearch = props.optimiseForLargeData === true ?
        (text === "undefined" || text === null || text === "" || text.length > 30)
        && (filtersURL === undefined || filtersURL === null || filtersURL === "")
        :
        state.firstSearch;
      const newState = {
        ...(state.text !== text) && {
          text: (text === "undefined" || text === null || text === "" || text.length > 30) ?
            ""
            :
            text
        },
        ...(state.page !== page) && {
          page: page === undefined || page === null || page === "" || isNaN(parsedPage) || parsedPage < 1 || parsedPage > 100 ?
            1
            :
            parsedPage
        },
        ...(state.lang !== lang) && {
          lang: (lang === undefined || lang === null || lang === "" || !props.projectLang.includes(lang)) ?
            props.defaultLang
            :
            lang
        },
        ...(state.component !== component) && {
          component: component === undefined || component === null || component === "" ||
            (!props.components.some(tx => tx.id === component) && component !== "All") ?
            "All"
            :
            component
        },
        ...(state.columns !== columns) && {
          columns: columns === undefined || columns === null || columns === "" ?
            ""
            :
            columns
        },
        ...(state.filtersURL !== filtersURL) && {
          filtersURL: filtersURL === undefined || filtersURL === null || filtersURL === "" ?
            ""
            :
            filtersURL,
          filters
        },
        firstSearch,
        ...(state.pageSize !== Number(pageSize)) && {
          pageSize: Number(pageSize)
        }
      };
      return newState;
    }
    return null;
  }

  computeBaseLinkLength = () => {
    let link = this.props.baseLink + '/project/editor#project=' + this.props.project;
    link += "&page=" + this.state.page;
    link += "&text=" + encodeURIComponent(this.state.text);
    link += "&lang=" + this.state.lang;
    link += "&component=" + this.state.component;
    link += "&columns=" + this.state.columns;
    link += "&pageSize=" + this.state.pageSize;
    return link.length;
  };

  updateSearch = (page, text = null, component, lang, columns, pageSize, filtersURL) => {
    this.setState({ initialSearch: false, firstSearch: false });
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    const segment = this.props.editMode ? 'editor' : 'search';
    let link = this.props.baseLink + '/project/' + segment + '#project=' + this.props.project;
    link += "&page=" + (page || this.state.page);
    link += "&text=" + (text !== null ? encodeURIComponent(text) : encodeURIComponent(this.state.text));
    link += "&lang=" + (lang || this.state.lang);
    link += "&component=" + (component || this.state.component);
    link += "&columns=" + (columns || this.state.columns);
    link += "&pageSize=" + (pageSize || this.state.pageSize);
    link += "&filters=" + (filtersURL !== undefined && filtersURL !== null ? filtersURL : this.state.filtersURL);
    this.props.history.push(link);
  };

  componentDidUpdate(prevProps, prevState) {
    // Note we don't do a page change for a change in columns
    if (!this.state.isLoadingClasses &&
      ((prevState.lang !== this.state.lang)
        || (prevState.text !== this.state.text)
        || (prevState.page !== this.state.page)
        || (prevState.component !== this.state.component)
        || (prevState.pageSize !== this.state.pageSize)
        || (prevState.filtersURL !== this.state.filtersURL)
      )) {
      this.handlePageChange();
    }
  }

  handlePagingChange = (page) => this.updateSearch(page);

  handleFilterChange = (text) => {
    if (this.state.slowSearch && text.length < 2) return;
    this.updateSearch(1, text);
  };

  handleComponentChange = (component) => this.updateSearch(1, null, component);

  changeLanguage = (lang) => this.updateSearch(1, null, null, lang);

  setColumns = async (columns, value, selected) => {
    if (selected) {
      await this.computeLinkInfo(this.state.resources, columns);
    }
    this.updateSearch(null, null, null, null, columns);
  };

  computeLinkInfo = async (resources, columns) => {
    try {
      let inComponentList = [];
      let doesNotExist = [];
      let componentsToLoad = [];
      let count = 0;
      const resourceInAlias = GS.getAlias(this.props.resourceIn);
      resources.forEach(c => {
        const cData = GS.data(c, this.props.graph, false);
        if (!cData) return [];
        if (cData[resourceInAlias]) {
          cData[resourceInAlias].forEach(c => {
            if (!componentsToLoad.includes(c)) componentsToLoad.push(c);
          });
        }
      });
      // We need to load any alt and hidden XL label entailements for the resources
      const entailmentTargets = resources.map(c => {
        const cData = GS.data(c, this.props.graph, false);
        if (!cData) return [];
        if (cData["xlAltLabel"] || cData["xlHiddenLabel"]) {
          const al = cData["xlAltLabel"] || [];
          const hl = cData["xlHiddenLabel"] || [];
          return [...al, ...hl];
        }
        else return [];
      }).flat();
      // Load any components and entailment to get them loaded in one go
      await this.loadData([...componentsToLoad, ...entailmentTargets], {}).promise;
      // We load one additional property at a time - all resources for that property
      for (let viewableProp of this.state.viewableOptions) {
        const visible = columns[count];
        count++;
        if (visible && (viewableProp.format === "relation" || viewableProp.joinAlias)) {
          const value = viewableProp.value;
          // We need to load the relationship targets in one go to avoid lots of requests
          const targets = resources.map(c => {
            const cData = GS.data(c, this.props.graph, false);
            if (!cData) return [];
            if (cData[resourceInAlias]) {
              cData[resourceInAlias].forEach(c => {
                if (!inComponentList.includes(c)) inComponentList.push(c);
              });
            }
            // We need to treat broader and related in a special way as they will use alias
            // But we don't want alias where joinAlias is used (e.g. scopeNote)
            const relationAlias = [
              "http://www.w3.org/2004/02/skos/core#broader",
              "http://www.w3.org/2004/02/skos/core#related"
            ].includes(value) ?
              GS.getAlias(value)
              :
              value;
            if (relationAlias in cData && cData[relationAlias] !== undefined) {
              const propValues = Array.isArray(cData[relationAlias]) ?
                // Might be in object form
                cData[relationAlias].map(item => typeof item === 'object' ? item.id : item)
                :
                [typeof cData[relationAlias] === 'object' ? cData[relationAlias].id : cData[relationAlias]];
              return propValues;
            }
            else return [];
          }).flat();
          const uniqueTargets = [...new Set([...targets])];
          await this.loadData(uniqueTargets, {}).promise;
          const dne = uniqueTargets.filter(iri => !GS.isLoaded(iri, this.props.graph));
          doesNotExist = doesNotExist.concat(dne);
        }
      }

      // And graph information for linked resources in other projects
      let componentLinkedProjects = [];
      let componentLinkedProjectsMap = cloneDeep(this.state.componentLinkedProjectsMap);
      if (inComponentList.length > 0) {
        await this.loadData(inComponentList, {}).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        inComponentList.forEach(ic => {
          if (GS.isLoaded(ic, this.props.graph)) {
            const inData = GS.data(ic, this.props.graph, false);
            if (inData && "associatedProject" in inData) {
              const linkedProjects = Array.isArray(inData.associatedProject) ? inData.associatedProject : [inData.associatedProject];
              componentLinkedProjects = componentLinkedProjects.concat(linkedProjects);
              linkedProjects.forEach(lp => {
                const lpGraph = getGraphFromProject(lp);
                if (!componentLinkedProjectsMap[lpGraph]) componentLinkedProjectsMap[lpGraph] = [];
                componentLinkedProjectsMap[lpGraph].push(ic);
              });
            }
          }
        });
      }

      const uniqueLinkedProjects = [...new Set(componentLinkedProjects)];
      doesNotExist = [...new Set(doesNotExist)];
      let lpCounter = 0;
      let graphInfo = cloneDeep(this.state.graphInfo);
      // Then we search linked projects for these resources
      while (doesNotExist.length > 0 && lpCounter < uniqueLinkedProjects.length) {
        const linkedGraph = getGraphFromProject(uniqueLinkedProjects[lpCounter]);
        await this.loadData([...doesNotExist, uniqueLinkedProjects[lpCounter]], {}, linkedGraph).promise;
        const doesNowExist = doesNotExist.filter(iri => GS.isLoaded(iri, linkedGraph));
        doesNowExist.forEach(iri => {
          if (!graphInfo[iri]) graphInfo[iri] = [];
          graphInfo[iri].push({ linkedGraph });
        });
        doesNotExist = doesNotExist.filter(iri => !GS.isLoaded(iri, linkedGraph));
        lpCounter++;
      }
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ graphInfo, componentLinkedProjectsMap });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  onPageSizeChange = (pageSize) => this.updateSearch(1, null, null, null, null, pageSize);

  clearSelected = () => this.setState({ selectedRows: [] });

  onSelectAll = (iriList) => {
    let newSelections = [...new Set([...this.state.selectedRows, ...iriList])];
    this.setState({ selectedRows: newSelections });
  };

  handleSelectionChange = (iri, status) => {
    let newSelections = [...this.state.selectedRows];
    if (!newSelections.includes(iri) && status) {
      newSelections.push(iri);
    }
    else {
      const index = newSelections.indexOf(iri);
      if (index > -1) {
        newSelections.splice(index, 1);
      }
    }
    this.setState({ selectedRows: newSelections });
  };

  convertColumns = (columns, viewableOptions) => {
    const columnValues = columns ?
      columns.split(",")
      :
      viewableOptions.map(vo => vo.visible === true ?
        'true'
        :
        vo.visible === false ?
          'false'
          :
          'undefined');
    const columnsAsBooleans = columnValues.map(co => co === 'true' ?
      true
      :
      co === 'false' ?
        false
        :
        undefined);
    return columnsAsBooleans;
  };

  onAddFilter = (col) => {
    this.setState({ addFilter: true, filterProperty: col.value });
  };

  addQuickFilter = (property, text, isExactMatch = false) => {
    const filters = this.state.filters;
    let newFilters = cloneDeep(filters);
    const index = newFilters.findIndex(f => f.property === property);
    if (index > -1) {
      newFilters.splice(index, 1);
    }
    newFilters.push({
      property,
      filterType: HAS_PROPERTY_WITH_VALUE,
      isExactMatch,
      value: [text]
    });
    const filtersURL = this.computeFiltersURL(newFilters);
    const URLlength = this.computeBaseLinkLength();
    if (filtersURL.length + URLlength > MAX_FILTER_LENGTH) {
      this.setState(WARNING_STATE);
      return;
    }
    this.setState({ filters: newFilters, addFilter: false, expanded: true }, () => {
      this.updateSearch(1, null, null, null, null, null, filtersURL);
    });
  };

  computeFiltersURL = (filters) => {
    const filtersURL = filters.map(f => {
      const tg = "targetGraph" in f ? encodeURIComponent(f.targetGraph) : "";
      let url = encodeURIComponent(f.property) + "," + f.filterType + "," + f.isExactMatch.toString() + "," + tg + ",";
      if (f.value) {
        const values = f.value.map(v => encodeURIComponent(v));
        url += values.join(",");
      }
      return url;
    }).join(";");
    return filtersURL;
  };

  addNewFilter = (property, filterType, values, isExactMatch = true, targetGraph) => {
    const filters = this.state.filters;
    let newFilters = cloneDeep(filters);
    newFilters.push({
      property,
      targetGraph: targetGraph && targetGraph !== this.props.graph ? targetGraph : "",
      filterType,
      isExactMatch,
      ...(values) && { value: [...values] }
    });
    const filtersURL = this.computeFiltersURL(newFilters);
    const URLlength = this.computeBaseLinkLength();
    if (filtersURL.length + URLlength > MAX_FILTER_LENGTH) {
      this.setState(WARNING_STATE);
      return;
    }
    this.setState({ filters: newFilters, addFilter: false, expanded: true }, () => {
      this.updateSearch(1, null, null, null, null, null, filtersURL);
    });
  };

  onRemoveFilter = (property) => {
    const filters = this.state.filters;
    let newFilters = cloneDeep(filters);
    const index = newFilters.findIndex(f => f.property === property);
    newFilters.splice(index, 1);
    const filtersURL = this.computeFiltersURL(newFilters);
    this.setState({ filters: newFilters }, () => {
      this.updateSearch(1, null, null, null, null, null, filtersURL);
    });
  };

  exitAddFilter = () => this.setState({ addFilter: false, filterProperty: "" });

  fullScreen = (fullScreen) => this.setState({ fullScreen });

  skipLoading = () => {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    this.setState({
      isLoading: false,
      skipUnfilteredSearches: true,
      firstSearch: false,
      initialSearch: false,
      slowSearch: false,
      page: 1,
      resources: [],
      pageCount: 1,
      searchID: uuidv4(),
      searchCount: 0
    });
  };

  render() {

    const { isLoading, resources, isErrored, page, pageCount, viewableOptions, pageSize, warningMessage, isWarning,
      lang, searchCount, isLoadingClasses, classResources, allowBulkChange, addFilter, filterProperty, filters,
      columns, visibleValues, selectedRows, fullScreen, expanded, component, graphInfo, componentLinkedProjectsMap,
      skipUnfilteredSearches, filtersURL } = this.state;
    const { defaultLang, graph, editMode, components, project, classes, entitlements, skosXL, baseLink, componentType, projectType,
      ontologyProjectAssociation, rowDisplay, editRequirements, resourceIn } = this.props;

    if (editMode && !allowBulkChange) {
      return <div data-test="no-access" style={{ paddingTop: 60, paddingRight: 30, textAlign: 'center' }}>
        <Typography align="center" variant="body2">Editing is not available with your current subscription.</Typography>
        <Typography align="center" variant="body2">This feature requires a minimum of a Premium subscription.</Typography>
        <Upgrade />
      </div>;
    }

    if (isErrored) return <Errored dataTest="as-error" data={isErrored} />;
    if (isLoadingClasses) return <Loading />;

    const isEntitledToEdit = entitlements.includes(editRequirements);
    if (editMode && !isEntitledToEdit) {
      return <div
        className={classes.advancedSearch}
        data-test="no-access">
        <div data-test="search-filtering" className={classes.filterArea}>
          <Grid container spacing={2} classes={{ "spacing-xs-2": this.props.classes.fullGrid }}>
            <Grid item xs={4}>
              <Typography className={classes.mainHeading} style={{ paddingTop: 0, marginTop: 0 }}>Editor</Typography>
            </Grid>
          </Grid>
        </div>
        <EntitlementWarning requiredEntitlements={[editRequirements]} />
      </div>;
    }

    if (!components || components.length === 0) {
      return <div style={{ paddingRight: 30 }}>
        <ActionText offset="180px">There are no components in this project.</ActionText>
      </div>;
    }

    const componentList = components.map(t => t.id);

    const classList = classResources
      .map(classItem => ({
        value: classItem.id,
        graph: classItem.inNamedGraph,
        label: classItem.label && classItem.label[defaultLang] ?
          classItem.label[defaultLang]
          :
          classItem.label["und"] ?
            classItem.label["und"]
            :
            classItem.id
      }))
      .sort((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) return - 1;
        return 1;
      });

    const fromResult = (page - 1) * pageSize + 1;
    const toResult = page * pageSize > searchCount ? searchCount : page * pageSize;
    const columnsAsBooleans = this.convertColumns(columns, viewableOptions);
    const resultsContainerHeight = pageCount > 1 ?
      filters.length > 0 ?
        expanded ?
          'calc(100% - 240px)'
          :
          'calc(100% - 110px)'
        :
        'calc(100% - 50px)'
      :
      filters.length > 0 ?
        expanded ?
          'calc(100% - 178px)'
          :
          'calc(100% - 50px)'
        :
        'calc(100% - 150px)';

    return (
      <div
        className={classes.advancedSearch}
        data-test="search-container"
        style={fullScreen ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, marginRight: 0, borderRadius: 0, zIndex: 500 } : {}}>
        <div data-test="search-filtering" className={classes.filterArea}>
          <Grid container spacing={2} classes={
            {
              "spacing-xs-2": this.props.classes.fullGrid
            }
          }>
            <Grid item xs={4}>
              {fullScreen &&
                <h5 data-test="page-layout-title" className={classes.layoutTitle}>
                  <ProjectSummary
                    id={project}
                    graph={graph}
                    lang={defaultLang}
                    handleNotAvailable={true} />
                </h5>
              }
              <Typography data-test="main-title" className={classes.mainHeading} style={{ paddingTop: 0, marginTop: fullScreen ? -12 : 0 }}>{
                editMode ?
                  'Editor'
                  :
                  'Advanced Search'
              }</Typography>
            </Grid>
            <Grid item xs={2}>
              <ComponentSelector
                id={project}
                defaultLang={defaultLang}
                selected={component}
                components={componentList}
                title={componentType}
                disabled={isLoading}
                graph={graph}
                handleChange={this.handleComponentChange} />
            </Grid>
            <Grid item xs={2}>
              <ProjectLanguageSelector
                disabled={isLoading}
                key={lang}
                selectedLang={lang}
                includeLabel={true}
                onSelect={this.changeLanguage} />
            </Grid>
            <Grid item xs={4} style={{ textAlign: 'right' }}>
              {fullScreen ?
                <AccessibleTooltip placement="right" title='Exit full screen'>
                  <IconButton aria-label="Exit full screen" data-test="exit-fullscreen" size="small" onClick={() => this.fullScreen(false)}>
                    <FullscreenExitIcon />
                  </IconButton>
                </AccessibleTooltip>
                :
                <AccessibleTooltip placement="right" title='Full screen'>
                  <IconButton aria-label="View full screen" data-test="fullscreen" size="small" onClick={() => this.fullScreen(true)}>
                    <FullscreenIcon />
                  </IconButton>
                </AccessibleTooltip>
              }
            </Grid>
          </Grid>
        </div>
        <div style={{
          marginBottom: 24, height: filters.length > 0 ?
            pageCount > 1 ?
              'calc(100% - 150px)'
              :
              'calc(100% - 160px)'
            :
            pageCount > 1 ?
              'calc(100% - 150px)'
              :
              '100%'
        }}>
          {
            isLoading || isLoadingClasses ?
              <div style={{ paddingTop: 160 }}>
                <Loading />
                {!skipUnfilteredSearches && filtersURL === "" &&
                  <div style={{ textAlign: 'center', marginTop: 12, marginBottom: 12 }}>
                    <SecondaryButton dataTest="skip-unfiltered" onClick={this.skipLoading} className={classes.button}>Skip Unfiltered Searches</SecondaryButton>
                  </div>
                }
              </div>
              :
              <React.Fragment>
                <div className={classes.searchResults} style={{ marginBottom: 12 }}>
                  {filters.length > 0 &&
                    <div data-test="filters-area" className={classes.filterArea} style={{ overflow: 'auto', padding: '12px 12px 0px 12px', marginBottom: 12 }}>
                      <div style={{ display: 'flex' }}>
                        <div style={{ flex: 1 }}>
                          <Typography className={classes.subHeading}>
                            <span>Filters</span>
                            {!expanded &&
                              <span data-test="filter-count" className={classes.count}>{filters.length}</span>
                            }
                          </Typography>
                        </div>
                        <div style={{ flex: 0, marginBottom: 4, transform: 'translateY(-4px)', marginRight: 6 }}>
                          <AccessibleTooltip title="Reset filters">
                            <IconButton aria-label="Reset filters" data-test="reset-filters" onClick={this.resetFilters} size="small">
                              <RotateLeftIcon className={classes.suggestionActionIcon} data-test="collapse" color="primary" />
                            </IconButton>
                          </AccessibleTooltip>
                        </div>
                        <div style={{ flex: 0, marginBottom: 4, transform: 'translateY(-4px)' }}>
                          {expanded ?
                            <AccessibleTooltip title="Hide filters">
                              <IconButton aria-label="Hide filters" data-test="hide-filters" onClick={this.collapseFilters} size="small">
                                <ExpandLess className={classes.suggestionActionIcon} data-test="collapse" color="primary" />
                              </IconButton>
                            </AccessibleTooltip>
                            :
                            <AccessibleTooltip title="Show filters">
                              <IconButton aria-label="Show filters" data-test="show-filters" onClick={this.expandFilters} size="small">
                                <ExpandMore className={classes.suggestionActionIcon} data-test="expand" color="primary" />
                              </IconButton>
                            </AccessibleTooltip>
                          }
                        </div>
                      </div>
                      {expanded &&
                        <div style={{
                          marginTop: 6,
                          ...(fullScreen) && { height: 126, overflow: 'scroll' }
                        }}>
                          {filters.map((filter, index) => {
                            const vo = viewableOptions.find(v => v.value === filter.property);
                            const format = vo.format;
                            const propertyName = vo.label;
                            return <div
                              data-test={"search-filter-" + index}
                              key={filter.property + "-" + filter.value}
                              style={{
                                float: 'left',
                                marginRight: 12
                              }}>
                              <SearchFilter
                                graph={graph}
                                classList={classList}
                                onRemoveFilter={this.onRemoveFilter}
                                components={components}
                                propertyName={propertyName}
                                projectType={projectType}
                                defaultLang={defaultLang}
                                isExactMatch={filter.isExactMatch}
                                project={project}
                                range={vo.range}
                                format={format}
                                targetGraph={filter.targetGraph}
                                filterType={filter.filterType}
                                property={filter.property}
                                value={filter.value} />
                            </div>;
                          })}
                        </div>
                      }
                    </div>
                  }
                  <div style={{ alignItems: 'flex-end', display: 'flex', flexWrap: 'wrap', marginRight: 12, width: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex' }}>
                        {editMode &&
                          <div style={{ marginLeft: 12, marginTop: 6, marginBottom: 6, marginRight: 12 }}>
                            <ProjectConsumer>
                              {({ ontologyProjectAssociation }) => (
                                <EditDataDialog
                                  disabled={isLoading || selectedRows.length === 0}
                                  classList={classList}
                                  project={project}
                                  components={components}
                                  enableSKOSchanges={projectType === "TaxonomyProject"}
                                  ontologyProjectAssociation={ontologyProjectAssociation}
                                  defaultLang={defaultLang}
                                  graph={graph}
                                  projectType={projectType}
                                  resourceList={selectedRows} />
                              )}
                            </ProjectConsumer>
                          </div>
                        }
                        {selectedRows.length > 0 &&
                          <div style={{
                            display: 'flex',
                            width: 190,
                            border: 'solid 1px #e5e5e5',
                            borderRadius: 4,
                            padding: '6px 6px 4px 6px',
                            marginLeft: 12
                          }}>
                            <div style={{ flex: 1, marginTop: 6 }}>
                              <Typography
                                data-test="selected-results"
                                className={classes.showing}
                                variant="body2">{selectedRows.length} Selected for Editing</Typography>
                              {selectedRows.length === 1000 && <Typography data-test="max-selections" variant="caption">Maximum reached.</Typography>}
                            </div>
                            <div style={{ flex: 0 }}>
                              <AccessibleTooltip title="Clear selected items">
                                <IconButton
                                  aria-label="Clear selected items"
                                  data-test="clear-selected"
                                  className={classes.smallIconButton}
                                  size="small"
                                  onClick={this.clearSelected}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </AccessibleTooltip>
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                    <div style={{ flex: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        {searchCount > 0 &&
                          <div style={{ width: 160, textAlign: 'right', marginRight: 24 }}>
                            <Typography
                              role="status"
                              data-test="showing-results"
                              variant="body2"
                              className={classes.showing}>Showing {fromResult} to {toResult} of {searchCount}</Typography>
                          </div>
                        }
                        {columnsAsBooleans &&
                          <div style={{ marginRight: 24, transform: 'translateY(7px)' }}>
                            <OptionsSelector
                              ariaLabel="Select column display options"
                              options={viewableOptions}
                              visibleValues={visibleValues}
                              values={columnsAsBooleans}
                              onSelect={this.setColumns} />
                          </div>
                        }
                        {resources && resources.length > 0 &&
                          <div style={{ width: 120, marginRight: 12 }}>
                            <MenuSelector
                              dataTest="page-size-selector"
                              label="Results to Show"
                              handleChange={this.onPageSizeChange}
                              selected={pageSize.toString()}
                              hintWord="results size"
                              options={[
                                {
                                  value: 50,
                                  label: "50"
                                },
                                {
                                  value: 100,
                                  label: "100"
                                },
                                {
                                  value: 200,
                                  label: "200"
                                }
                              ]} />
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  data-test="results-container"
                  data-height={resultsContainerHeight}
                  style={{
                    overflow: 'scroll',
                    ...(fullScreen) && { height: resultsContainerHeight }
                  }}>
                  <SearchResults
                    editMode={editMode}
                    graph={graph}
                    classSelector={classList && classList.length > 0 ?
                      <MenuSelector
                        dataTest="class-selector"
                        selected={""}
                        hintWord="Class"
                        handleChange={(value) => this.addQuickFilter("http://www.w3.org/1999/02/22-rdf-syntax-ns#type", value)}
                        options={[
                          ...classList
                        ]} />
                      :
                      null
                    }
                    selectedRows={selectedRows}
                    rowDisplay={rowDisplay}
                    graphInfo={graphInfo}
                    componentLinkedProjectsMap={componentLinkedProjectsMap}
                    project={project}
                    resourceIn={resourceIn}
                    projectType={projectType}
                    baseLink={baseLink}
                    helpOnNoResults={filters.length === 0 && skipUnfilteredSearches}
                    allowFilters={filters.length < 5}
                    addFilter={this.onAddFilter}
                    onAddQuickFilter={this.addQuickFilter}
                    ontologyProjectAssociation={ontologyProjectAssociation}
                    excludeFilterProperties={filters.map(f => f.property)}
                    columns={columnsAsBooleans}
                    visibleValues={visibleValues}
                    viewOptions={viewableOptions}
                    onSelectAll={this.onSelectAll}
                    handleSelectionChange={this.handleSelectionChange}
                    resources={resources}
                    components={components || []}
                    defaultLang={defaultLang}
                    viewLang={lang} />
                </div>
              </React.Fragment>
          }
        </div>
        {!isLoading && <Paging page={page} pageCount={pageCount} handlePageChange={this.handlePagingChange} />}
        {addFilter &&
          <AddFilterDialog
            graph={graph}
            lang={lang}
            project={project}
            components={components || []}
            exclude={filters.filter(f => f.property === filterProperty).map(f => f.value)}
            defaultLang={defaultLang}
            ontologyProjectAssociation={ontologyProjectAssociation}
            skosXL={skosXL}
            projectType={projectType}
            classList={classList}
            data={viewableOptions.find(v => v.value === filterProperty)}
            onExitAddFilter={this.exitAddFilter}
            onAddFilter={this.addNewFilter}
          />
        }
        {isWarning && <Warning message={warningMessage} onExitWarning={this.onExitWarning} />}
      </div>
    );
  }

}

AdvancedSearchContainer.defaultProps = {
  loadInfo: [],
  viewOptions: [],
  ontologyProjectAssociation: [],
  editMode: false,
  optimiseForLargeData: false
};

AdvancedSearchContainer.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.object,
  baseLink: PropTypes.string.isRequired,
  history: PropTypes.object,
  location: PropTypes.object,
  editMode: PropTypes.bool,
  editRequirements: PropTypes.string,
  rowDisplay: PropTypes.object.isRequired,
  searchClass: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  optimiseForLargeData: PropTypes.bool,
  resourceIn: PropTypes.string.isRequired,
  projectLang: PropTypes.array.isRequired,
  ontologyProjectAssociation: PropTypes.array,
  entitlements: PropTypes.array.isRequired,
  skosXL: PropTypes.bool.isRequired,
  graph: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  projectType: PropTypes.string.isRequired,
  components: PropTypes.array,
  componentType: PropTypes.string,
  defaultLang: PropTypes.string.isRequired
};

export default withRouter(withStyles(styles)(AdvancedSearchContainer));